import { useState } from 'react';
import { Calendar, MapPin, Clock, Check, X, Users } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface EventData {
  title: string;
  date: string; // ISO string
  time?: string;
  location?: string;
  description?: string;
  rsvps?: Record<string, 'going' | 'maybe' | 'not_going'>;
  recurring?: string;
  reminders?: boolean;
}

interface EventBubbleProps {
  data: EventData;
  isOwn: boolean;
  currentUserId?: string;
  onRsvp?: (status: 'going' | 'maybe' | 'not_going') => void;
}

export function EventBubble({ data, isOwn, currentUserId, onRsvp }: EventBubbleProps) {
  const rsvps = data.rsvps || {};
  const myRsvp = currentUserId ? rsvps[currentUserId] : undefined;
  const goingCount = Object.values(rsvps).filter(v => v === 'going').length;
  const maybeCount = Object.values(rsvps).filter(v => v === 'maybe').length;

  const eventDate = new Date(data.date);
  const isPast = eventDate < new Date();

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={cn(
          "rounded-2xl overflow-hidden max-w-[300px] w-full",
          isOwn
            ? "bg-[hsl(200,80%,40%)] text-white"
            : "bg-[#F0F0F0] dark:bg-[#2A2A2A] text-foreground"
        )}
      >
        {/* Header band */}
        <div className={cn(
          "px-4 py-2.5 flex items-center gap-2",
          isOwn ? "bg-white/10" : "bg-primary/10"
        )}>
          <Calendar className="h-4 w-4 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider">Event</span>
        </div>

        <div className="px-4 py-3 space-y-2">
          <h4 className="text-base font-bold leading-tight">{data.title}</h4>

          <div className="flex items-center gap-2 text-sm opacity-80">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>{format(eventDate, 'EEE, d MMM yyyy')}{data.time ? ` · ${data.time}` : ''}</span>
          </div>

          {data.location && (
            <div className="flex items-center gap-2 text-sm opacity-80">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{data.location}</span>
            </div>
          )}

          {data.description && (
            <p className="text-sm opacity-70 leading-snug">{data.description}</p>
          )}

          {data.recurring && (
            <p className="text-xs opacity-60">🔁 {data.recurring}</p>
          )}

          {/* RSVP counts */}
          <div className="flex items-center gap-3 pt-1 text-xs opacity-60">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {goingCount} going</span>
            {maybeCount > 0 && <span>{maybeCount} maybe</span>}
          </div>

          {/* RSVP buttons */}
          {!isPast && onRsvp && (
            <div className="flex gap-1.5 pt-1">
              {(['going', 'maybe', 'not_going'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => onRsvp(status)}
                  className={cn(
                    "flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all",
                    myRsvp === status
                      ? isOwn ? "bg-white/30 text-white" : "bg-primary/20 text-primary"
                      : isOwn ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-muted hover:bg-muted/80 text-muted-foreground"
                  )}
                >
                  {status === 'going' ? '✓ Going' : status === 'maybe' ? '? Maybe' : '✗ No'}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
